import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Grid,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import * as XLSX from 'xlsx';
import { useTranslation } from 'react-i18next';
import { useAuth, type Warehouse } from '../../../contexts/AuthContext';
import { ACCOUNTING_API } from '../../../config/api';
import { accountingApi } from '../../../config/apiClient';
import AccountingDataTable from '../shared/AccountingDataTable';
import type { DataTableColumn } from '../shared/AccountingDataTable';

interface Props {
  /** Yöneticinin şubesine bağlı depolar; backend yalnızca bunları kabul eder. */
  warehouses: Warehouse[];
}

interface MovementRow {
  id: string;
  createdAt: string;
  movementType: string;
  productName: string;
  stockCode: string;
  previousQuantity: number;
  newQuantity: number;
  change: number;
  unit: string;
  totalAmount: number;
  currency: string;
  accountName: string;
  description: string;
  createdBy: string;
  referenceType: string;
  referenceNumber: string;
}

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 400;

const MOVEMENT_TYPE_LABELS: Record<string, string> = {
  sale: 'Satış',
  manual_sale: 'Manuel Satış',
  purchase: 'Alış',
  adjustment: 'Stok Düzeltme',
  transfer: 'Transfer',
  QUICK_SALE: 'Hızlı Satış',
  quick_sale: 'Hızlı Satış',
  SALE: 'Satış',
  PURCHASE: 'Alış',
  TRANSFER_IN: 'Depo Girişi (Transfer)',
  TRANSFER_OUT: 'Depo Çıkışı (Transfer)',
  ADJUSTMENT_IN: 'Stok Düzeltme (Giriş)',
  ADJUSTMENT_OUT: 'Stok Düzeltme (Çıkış)',
  RETURN_SALE: 'Satış İadesi',
  RETURN_PURCHASE: 'Alış İadesi',
  PRODUCTION_IN: 'Üretimden Giriş',
  PRODUCTION_OUT: 'Üretime Sarf',
  PRODUCTION_REVERSAL: 'Üretim İptali',
  SALES_INVOICE: 'Satış Faturası',
  PURCHASE_INVOICE: 'Alış Faturası',
  SALES_ORDER: 'Satış Siparişi',
  PURCHASE_ORDER: 'Alış Siparişi',
  MANUAL_ADJUSTMENT: 'Manuel Stok Düzeltme',
  sale_due_to_edit: 'Düzenleme Sonrası Satış',
  sale_return_due_to_edit: 'Düzenleme Sonrası İade',
};

const MOVEMENT_TYPE_FILTERS = [
  'QUICK_SALE', 'SALES_INVOICE', 'PURCHASE_INVOICE', 'MANUAL_ADJUSTMENT',
  'TRANSFER_IN', 'TRANSFER_OUT', 'sale', 'purchase', 'adjustment', 'transfer',
];

const movementTypeLabel = (type: string) => MOVEMENT_TYPE_LABELS[type] ?? type;

const num = (v: any) => (v === null || v === undefined || v === '' ? 0 : Number(v) || 0);

const mapMovement = (d: any): MovementRow => {
  const previousQuantity = num(d.previousQuantity ?? d.PreviousQuantity);
  const newQuantity = num(d.newQuantity ?? d.NewQuantity);
  return {
    id: String(d.id ?? d.Id ?? ''),
    createdAt: d.createdAt ?? d.CreatedAt ?? d.date ?? d.Date ?? '',
    movementType: d.movementType ?? d.MovementType ?? '',
    productName: d.productName ?? d.ProductName ?? '',
    stockCode: d.stockCode ?? d.StockCode ?? '',
    previousQuantity,
    newQuantity,
    change: newQuantity - previousQuantity,
    unit: d.unit ?? d.Unit ?? '',
    totalAmount: num(d.totalAmount ?? d.TotalAmount),
    currency: d.currency ?? d.Currency ?? 'TRY',
    accountName: d.accountName ?? d.AccountName ?? '',
    description: d.description ?? d.Description ?? '',
    createdBy: d.createdBy ?? d.CreatedBy ?? '',
    referenceType: d.referenceType ?? d.ReferenceType ?? '',
    referenceNumber: d.referenceNumber ?? d.ReferenceNumber ?? '',
  };
};

/** Backend UTC yazar; eski kayıtlarda "Z" eksik olabilir. */
const toDate = (value: string) => {
  const hasTimezone = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(value);
  return new Date(hasTimezone ? value : `${value}Z`);
};

const WarehouseStockMovements: React.FC<Props> = ({ warehouses }) => {
  const { i18n } = useTranslation();
  const { isModuleEnabled, enabledModules } = useAuth();
  const showProductionFilters = enabledModules == null || isModuleEnabled('production');
  const isTr = i18n.language.startsWith('tr');
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);
  const locale = isTr ? 'tr-TR' : 'en-US';

  const [selectedWarehouse, setSelectedWarehouse] = useState<Warehouse | null>(warehouses[0] ?? null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [movementType, setMovementType] = useState<string | null>(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [rows, setRows] = useState<MovementRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [totalAmount, setTotalAmount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fetchId = useRef(0);

  useEffect(() => {
    if (selectedWarehouse && warehouses.some(w => w.id === selectedWarehouse.id)) return;
    setSelectedWarehouse(warehouses[0] ?? null);
  }, [warehouses, selectedWarehouse]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const buildParams = useCallback(() => ({
    warehouseId: selectedWarehouse?.id,
    search: debouncedSearch || undefined,
    movementType: movementType || undefined,
    startDate: startDate ? new Date(`${startDate}T00:00:00`).toISOString() : undefined,
    endDate: endDate ? new Date(`${endDate}T23:59:59.999`).toISOString() : undefined,
  }), [selectedWarehouse, debouncedSearch, movementType, startDate, endDate]);

  const loadPage = useCallback(async (pageIndex: number, size: number) => {
    if (!selectedWarehouse) return;
    const currentFetch = ++fetchId.current;
    setLoading(true);
    setError(null);
    try {
      const response = await accountingApi.get(
        ACCOUNTING_API.STOCK_MOVEMENTS_PAGED({ ...buildParams(), page: pageIndex + 1, pageSize: size })
      );
      if (currentFetch !== fetchId.current) return;
      const result = response.data;
      if (!result?.success) throw new Error(result?.message || 'Stok hareketleri yüklenemedi');
      setRows((result.data || []).map(mapMovement));
      setTotalCount(result.totalCount || 0);
      setTotalAmount(num(result.aggregates?.totalAmount));
      setPage(pageIndex);
      setPageSize(size);
    } catch (err: any) {
      if (currentFetch !== fetchId.current) return;
      console.error('[WarehouseStockMovements] Yükleme hatası:', err);
      setRows([]);
      setTotalCount(0);
      setError(err?.response?.data?.message || tt('Stok hareketleri yüklenirken hata oluştu.', 'Error loading stock movements.'));
    } finally {
      if (currentFetch === fetchId.current) setLoading(false);
    }
    // tt dil değişiminde yeniden oluşur; yükleme tetiklememeli
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWarehouse, buildParams]);

  useEffect(() => {
    loadPage(0, pageSize);
    // pageSize değişimi onPageChange üzerinden yüklenir
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadPage]);

  const formatDate = useCallback((value: string) => {
    if (!value) return '-';
    return new Intl.DateTimeFormat(locale, {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Europe/Istanbul',
    }).format(toDate(value));
  }, [locale]);

  const formatQty = useCallback((value: number, signed = false) => {
    const formatted = (Math.round(value * 10000) / 10000).toLocaleString(locale, { maximumFractionDigits: 4 });
    return signed && value > 0 ? `+${formatted}` : formatted;
  }, [locale]);

  const formatMoney = useCallback((value: number, currency = 'TRY') =>
    new Intl.NumberFormat(locale, { style: 'currency', currency: currency || 'TRY', minimumFractionDigits: 2 }).format(value),
  [locale]);

  const columns = useMemo<DataTableColumn<MovementRow>[]>(() => [
    { id: 'createdAt', label: tt('Tarih', 'Date'), render: r => formatDate(r.createdAt), minWidth: '130px' },
    {
      id: 'movementType',
      label: tt('İşlem Tipi', 'Type'),
      render: r => (
        <Box>
          <Chip size="small" variant="outlined" label={movementTypeLabel(r.movementType)} />
          {(r.referenceType === 'PRODUCTION' || r.referenceType === 'PRODUCTION_CANCEL') && r.referenceNumber ? (
            <Typography variant="caption" color="text.secondary" display="block">{r.referenceNumber}</Typography>
          ) : null}
        </Box>
      ),
    },
    {
      id: 'productName',
      label: tt('Ürün', 'Product'),
      render: r => (
        <Box>
          <Typography variant="body2" fontWeight={600}>{r.productName}</Typography>
          {r.stockCode && <Typography variant="caption" color="text.secondary">{r.stockCode}</Typography>}
        </Box>
      ),
      minWidth: '180px',
    },
    { id: 'previousQuantity', label: tt('Önceki', 'Previous'), align: 'right', render: r => formatQty(r.previousQuantity) },
    {
      id: 'change',
      label: tt('Değişim', 'Change'),
      align: 'right',
      render: r => (
        <Typography
          variant="body2"
          fontWeight={700}
          color={r.change < 0 ? 'error.main' : r.change > 0 ? 'success.main' : 'text.secondary'}
        >
          {formatQty(r.change, true)}
        </Typography>
      ),
    },
    { id: 'newQuantity', label: tt('Yeni', 'New'), align: 'right', render: r => `${formatQty(r.newQuantity)} ${r.unit}`.trim() },
    { id: 'totalAmount', label: tt('Tutar', 'Amount'), align: 'right', render: r => formatMoney(r.totalAmount, r.currency) },
    { id: 'accountName', label: tt('Cari', 'Account'), render: r => r.accountName || '-', tooltip: true },
    { id: 'description', label: tt('Açıklama', 'Description'), render: r => r.description || '-', tooltip: true, minWidth: '160px' },
    { id: 'createdBy', label: tt('Yapan', 'By'), render: r => r.createdBy || '-', tooltip: true },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [formatDate, formatQty, formatMoney, isTr]);

  const handleExport = async () => {
    if (!selectedWarehouse) return;
    setExporting(true);
    setError(null);
    try {
      const response = await accountingApi.get(
        ACCOUNTING_API.STOCK_MOVEMENTS_PAGED({ ...buildParams(), page: 1, pageSize: 50000, export: true })
      );
      const data: MovementRow[] = (response.data?.data || []).map(mapMovement);
      if (data.length === 0) {
        setError(tt('Aktarılacak hareket bulunamadı.', 'No movements to export.'));
        return;
      }
      const sheet = XLSX.utils.json_to_sheet(data.map((r, i) => ({
        'Sıra': i + 1,
        'Tarih': formatDate(r.createdAt),
        'İşlem Tipi': movementTypeLabel(r.movementType),
        'Belge No': r.referenceNumber,
        'Ürün': r.productName,
        'Stok Kodu': r.stockCode,
        'Önceki Miktar': r.previousQuantity,
        'Miktar Değişimi': r.change,
        'Yeni Miktar': r.newQuantity,
        'Birim': r.unit,
        'Tutar': r.totalAmount,
        'Para Birimi': r.currency,
        'Cari': r.accountName,
        'Açıklama': r.description,
        'Yapan': r.createdBy,
      })));
      sheet['!cols'] = [6, 18, 22, 18, 32, 14, 14, 14, 14, 8, 14, 10, 24, 40, 24].map(wch => ({ wch }));
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, sheet, 'Stok Hareketleri');
      const now = new Date();
      const safeName = (selectedWarehouse.name || 'Depo').replace(/[\\/:*?"<>|]/g, '_');
      XLSX.writeFile(workbook, `Stok_Hareketleri_${safeName}_${now.getDate()}.${now.getMonth() + 1}.${now.getFullYear()}.xlsx`);
    } catch (err: any) {
      console.error('[WarehouseStockMovements] Excel hatası:', err);
      setError(err?.response?.data?.message || tt('Excel dışa aktarımında hata oluştu.', 'Error exporting to Excel.'));
    } finally {
      setExporting(false);
    }
  };

  const hasFilters = !!(searchTerm || movementType || startDate || endDate);

  return (
    <Box sx={{ width: '100%' }}>
      <Grid container spacing={1.5} sx={{ mb: 2 }} alignItems="center">
        <Grid item xs={12} md={3}>
          <Autocomplete
            size="small"
            options={warehouses}
            getOptionLabel={o => o.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            value={selectedWarehouse}
            onChange={(_, v) => setSelectedWarehouse(v)}
            disableClearable={!!selectedWarehouse}
            renderInput={params => (
              <TextField
                {...params}
                label={tt('Depo', 'Warehouse')}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment position="start"><WarehouseIcon fontSize="small" /></InputAdornment>
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            size="small"
            label={tt('Ürün / açıklama ara', 'Search product / description')}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <Autocomplete
            size="small"
            options={showProductionFilters
              ? [...MOVEMENT_TYPE_FILTERS, 'PRODUCTION_IN', 'PRODUCTION_OUT', 'PRODUCTION_REVERSAL']
              : MOVEMENT_TYPE_FILTERS}
            getOptionLabel={movementTypeLabel}
            value={movementType}
            onChange={(_, v) => setMovementType(v)}
            renderInput={params => <TextField {...params} label={tt('İşlem tipi', 'Movement type')} />}
          />
        </Grid>
        <Grid item xs={6} md={1.5}>
          <TextField
            fullWidth
            size="small"
            type="date"
            label={tt('Başlangıç', 'From')}
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={6} md={1.5}>
          <TextField
            fullWidth
            size="small"
            type="date"
            label={tt('Bitiş', 'To')}
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} md={1} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            fullWidth
            variant="contained"
            color="success"
            startIcon={<FileDownloadIcon />}
            onClick={handleExport}
            disabled={exporting || loading || !selectedWarehouse || totalCount === 0}
          >
            {exporting ? '...' : 'Excel'}
          </Button>
        </Grid>
      </Grid>

      <Paper variant="outlined" sx={{ p: 1.5, mb: 2, display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center', borderRadius: 2 }}>
        <Typography variant="body2">
          {tt('Hareket sayısı', 'Movements')}: <strong>{totalCount.toLocaleString(locale)}</strong>
        </Typography>
        <Typography variant="body2">
          {tt('Toplam tutar', 'Total amount')}: <strong>{formatMoney(totalAmount)}</strong>
        </Typography>
        {hasFilters && (
          <Button
            size="small"
            onClick={() => { setSearchTerm(''); setMovementType(null); setStartDate(''); setEndDate(''); }}
            sx={{ ml: 'auto' }}
          >
            {tt('Filtreleri temizle', 'Clear filters')}
          </Button>
        )}
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <AccountingDataTable<MovementRow>
        columns={columns}
        rows={rows}
        getRowId={r => r.id}
        loading={loading}
        elevation={3}
        tableLayout="auto"
        emptyMessage={tt('Bu depoda seçilen kriterlere uygun hareket yok.', 'No movements match the selected criteria.')}
        columnResizeStorageKey="warehouse-stock-movements"
        serverSidePagination
        totalCount={totalCount}
        page={page}
        pageSize={pageSize}
        onPageChange={(p, size) => loadPage(p, size)}
      />
    </Box>
  );
};

export default WarehouseStockMovements;
